import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/sections/Header";
import Footer from "@/sections/Footer";
import { Trophy, Users, Rocket, Flame } from "lucide-react";

const RANK_STYLE = {
  1: "text-[#FFD700]",
  2: "text-[#C0C0C0]",
  3: "text-[#CD7F32]",
};

export default function Leaderboard() {
  const [leaders, setLeaders] = useState(null);
  const [streaks, setStreaks] = useState([]);

  useEffect(() => {
    api.get("/referrals/leaderboard")
      .then(({ data }) => setLeaders(data.leaders))
      .catch(() => setLeaders([]));
    api.get("/streaks/leaderboard")
      .then(({ data }) => setStreaks(data.leaders))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="pt-24 pb-20" data-testid="leaderboard-page">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#cfff04] mb-3">
            // Referral leaderboard
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter mb-3">
            Top recruiters.
          </h1>
          <p className="text-[#e2e2e2] mb-10 max-w-xl">
            Every creator gets a referral link. Bring people to SolSpur — signups and paid launches
            you drive land you on this board.
          </p>

          {leaders === null ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={`lb-skeleton-${i}`} className="border border-[#3a3a3a] bg-[#121212] h-16 animate-pulse" />
              ))}
            </div>
          ) : leaders.length === 0 ? (
            <div className="border border-[#cfff04] bg-[#121212] p-10 text-center" data-testid="leaderboard-empty">
              <Trophy size={32} className="mx-auto text-[#cfff04] mb-4" />
              <p className="font-display text-2xl font-black uppercase tracking-tighter mb-2">
                The board is wide open.
              </p>
              <p className="text-sm text-[#e2e2e2] mb-6 max-w-md mx-auto">
                No referrals yet — grab your link from your creator dashboard and claim the #1 spot.
              </p>
              <Link
                to="/my-coins"
                data-testid="leaderboard-get-link-cta"
                className="btn-brutal inline-flex bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-sm px-8 py-4"
              >
                Get my referral link
              </Link>
            </div>
          ) : (
            <>
              <div className="hidden sm:grid grid-cols-12 gap-4 px-5 pb-2 font-mono text-[11px] uppercase tracking-widest text-[#d0d0d0]">
                <div className="col-span-1">#</div>
                <div className="col-span-7">Creator</div>
                <div className="col-span-2 text-right">Signups</div>
                <div className="col-span-2 text-right">Paid launches</div>
              </div>
              <div className="space-y-2" data-testid="leaderboard-list">
                {leaders.map((l) => (
                  <div
                    key={`leader-${l.rank}`}
                    data-testid={`leaderboard-row-${l.rank}`}
                    className="border border-[#3a3a3a] bg-[#121212] px-5 py-4 grid grid-cols-12 gap-4 items-center hover:bg-[#1a1a1a] transition-colors"
                  >
                    <div className={`col-span-2 sm:col-span-1 font-display font-black text-xl ${RANK_STYLE[l.rank] || "text-[#e2e2e2]"}`}>
                      {l.rank}
                    </div>
                    <div className="col-span-10 sm:col-span-7 flex items-center gap-3 min-w-0">
                      {l.picture ? (
                        <img src={l.picture} alt="" className="w-8 h-8 border border-[#3a3a3a] object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-8 h-8 border border-[#3a3a3a] bg-[#050505] flex items-center justify-center font-mono text-xs text-[#cfff04] flex-shrink-0">
                          {(l.name || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className="font-display font-bold uppercase truncate">{l.name}</span>
                    </div>
                    <div className="col-span-6 sm:col-span-2 sm:text-right font-mono text-sm">
                      <Users size={11} className="inline mr-1.5 -mt-0.5 sm:hidden text-[#d0d0d0]" />
                      {l.signups}
                    </div>
                    <div className="col-span-6 sm:col-span-2 sm:text-right font-mono text-sm text-[#00ff41]">
                      <Rocket size={11} className="inline mr-1.5 -mt-0.5 sm:hidden text-[#d0d0d0]" />
                      {l.paid_launches}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 text-center">
                <Link
                  to="/my-coins"
                  data-testid="leaderboard-join-cta"
                  className="btn-brutal inline-flex bg-[#cfff04] text-[#050505] font-bold uppercase tracking-wider text-sm px-8 py-4"
                >
                  Get my referral link
                </Link>
              </div>
            </>
          )}

          {streaks.length > 0 && (
            <div className="mt-14" data-testid="streak-leaderboard">
              <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#ff6b35] mb-3 flex items-center gap-2">
                <Flame size={12} /> Share streaks
              </div>
              <h2 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tighter mb-2">
                Most consistent creators.
              </h2>
              <p className="text-[#e2e2e2] text-sm mb-6 max-w-xl">
                One share a day keeps the flame alive — these creators never miss.
              </p>
              <div className="space-y-2" data-testid="streak-leaderboard-list">
                {streaks.map((s) => (
                  <div
                    key={`streak-${s.rank}`}
                    data-testid={`streak-row-${s.rank}`}
                    className="border border-[#2a1a12] bg-[#121212] px-5 py-3 grid grid-cols-12 gap-4 items-center hover:bg-[#140c08] transition-colors"
                  >
                    <div className={`col-span-2 sm:col-span-1 font-display font-black text-lg ${RANK_STYLE[s.rank] || "text-[#e2e2e2]"}`}>
                      {s.rank}
                    </div>
                    <div className="col-span-6 sm:col-span-7 flex items-center gap-3 min-w-0">
                      {s.picture ? (
                        <img src={s.picture} alt="" className="w-7 h-7 border border-[#3a3a3a] object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-7 h-7 border border-[#3a3a3a] bg-[#050505] flex items-center justify-center font-mono text-xs text-[#ff6b35] flex-shrink-0">
                          {(s.name || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className="font-display font-bold uppercase truncate text-sm">{s.name}</span>
                    </div>
                    <div className="col-span-2 text-right font-mono text-sm text-[#ff6b35]">
                      <Flame size={11} className="inline mr-1 -mt-0.5" />
                      {s.best_streak}d
                    </div>
                    <div className="col-span-2 text-right font-mono text-[11px] uppercase tracking-wider text-[#d0d0d0]">
                      {s.current_streak > 0 ? `${s.current_streak}d live` : "cold"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
